import { motion } from 'framer-motion';
import { Plus, RefreshCw, Trash2, Play } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import EmptyState from '../../components/EmptyState';
import { EmptyRecurring } from '../../components/illustrations';

const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function RecurringTab({ ctx }) {
  const {
    isAdmin,
    recurringExpenses,
    setShowRecurringModal,
    handleTriggerRecurring,
    handleToggleRecurring,
    handleDeleteRecurring,
    formatCurrency,
  } = ctx;

  return (
    <div data-testid="recurring-tab-body" className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end mb-2">
          <Button
            onClick={() => setShowRecurringModal(true)}
            data-testid="add-recurring-btn"
            className="rounded-full"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Recurring
          </Button>
        </div>
      )}

      {recurringExpenses.length === 0 ? (
        <EmptyState
          illustration={EmptyRecurring}
          title="No recurring expenses"
          description={isAdmin ? 'Set up auto-repeating expenses for weekly sessions or rent.' : 'Admins can set up recurring expenses here.'}
          testId="no-recurring"
        />
      ) : (
        <div className="space-y-3">
          {recurringExpenses.map((recurring, index) => (
            <motion.div
              key={recurring.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.05 }}
            >
              <Card
                className={`p-4 border-2 rounded-2xl ${!recurring.is_active ? 'opacity-60' : ''}`}
                data-testid={`recurring-item-${recurring.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <RefreshCw className={`w-4 h-4 ${recurring.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                      <h4 className="font-semibold">{recurring.description}</h4>
                    </div>
                    <p className="font-display text-xl font-bold amount-display">
                      {formatCurrency(recurring.amount)}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-semibold capitalize">
                        {recurring.frequency === 'custom_dates' ? 'Specific Dates' : recurring.frequency}
                      </span>
                      {recurring.days_of_week?.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {recurring.days_of_week.map((d) => dayNames[d]).join(', ')}
                        </span>
                      )}
                      {recurring.custom_dates?.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {recurring.custom_dates.slice(0, 3).join(', ')}
                          {recurring.custom_dates.length > 3 ? ` +${recurring.custom_dates.length - 3} more` : ''}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Paid by {recurring.paid_by_name}</p>
                  </div>

                  {isAdmin && (
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTriggerRecurring(recurring.id)}
                        data-testid={`trigger-recurring-${recurring.id}`}
                        className="rounded-full"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Create
                      </Button>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={recurring.is_active}
                          onCheckedChange={() => handleToggleRecurring(recurring.id)}
                          data-testid={`toggle-recurring-${recurring.id}`}
                        />
                        <button
                          onClick={() => handleDeleteRecurring(recurring.id)}
                          className="text-destructive hover:text-destructive/80"
                          data-testid={`delete-recurring-${recurring.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
